/**
 * Sales Rep Performance — the monthly rep workbook, every rep at once, plus Janus.
 *
 * Top to bottom:
 *
 * 1. **Company** tiles: the month and fiscal year to date against budget and
 *    prior year (base business only).
 * 2. **All reps**: FYTD actual / budget / prior year per rep, what each is over
 *    or under budget by, and the workbook table one row per rep. Clicking a rep
 *    opens them; the rep is a URL parameter, so a link reproduces the view.
 * 3. **One rep**: the month-by-month trend, the workbook table by end user, and
 *    the drivers — which end users and product lines moved the variance.
 * 4. **Janus**, with no budget: volume, placements and reorders by rep, then
 *    one rep's SKUs and every ship-to x SKU with its opening order and reorders.
 *
 * Every figure comes from one payload. The page filters by rep and unit; it
 * never computes a comparison the server did not.
 */

import { useMemo, useState } from 'react'

import type { EChartsOption } from 'echarts'

import type { CustomReportProps } from '..'
import type { Cell, DatasetResult, ReportResult } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { dataset, type Keyed, text } from '../shared/rows'
import '../report.css'
import {
  amount,
  axisFormatter,
  COLOURS,
  GRID,
  LEGEND_TOP,
  num,
  pct,
  rankSize,
  ratio,
  shortItem,
  signed,
  STATUS_ORDER,
  STATUS_TONE,
  statusColour,
  tone,
  totals,
  type Unit,
  UNIT_NAME,
} from './format'
import { ChartFrame, Kpi } from './parts'
import { BulletPair, ProgressBullets, RepBridge } from './charts'
import { JanusPictures } from './janus'
import { CaveatList } from '../../components/CaveatList'

const ALL = '(all)'

/**
 * The payload with every dataset that names a rep cut to the team's reps: one
 * filter, so the tiles, charts, tables, Janus and the forecast view cannot
 * disagree about who is in the team. A rep in no team is in no team's view.
 */
function forTeam(result: ReportResult, reps: ReadonlySet<string>): ReportResult {
  const datasets: Record<string, DatasetResult> = {}
  for (const [key, data] of Object.entries(result.datasets)) {
    const at = data.columns.findIndex((column) => column.key === 'sales_rep')
    if (at < 0) {
      datasets[key] = data
      continue
    }
    const rows = data.rows.filter((row) => reps.has(String(row[at] ?? '')))
    datasets[key] = { ...data, rows, row_count: rows.length }
  }
  return { ...result, datasets }
}

export function RepPerformance({ result: raw, values, setParam }: CustomReportProps) {
  const unit: Unit = values['unit'] === 'fob' ? 'fob' : 'gal'
  const teamValue = typeof values['team'] === 'string' && values['team'] !== '' ? values['team'] : ALL
  const team = teamValue === ALL ? null : teamValue
  const result =
    team === null
      ? raw
      : forTeam(
          raw,
          new Set(
            dataset(raw, 'rep_teams')
              .filter((row) => row['team'] === team)
              .map((row) => text(row['sales_rep']) ?? ''),
          ),
        )
  const rep = typeof values['rep'] === 'string' && values['rep'] !== '' ? values['rep'] : ALL
  const chosen = rep === ALL ? null : rep
  const header = dataset(result, 'header')[0]
  // The month decides the page (owner, 2026-10-18): a month still open is
  // the forecast -- shipped, booked and what is still expected against
  // budget, by rep and by customer -- with Janus at the foot; the year, the
  // bridge and the detail tables are for a month that has closed.
  const view = text(header?.['is_partial']) === 'true' ? 'forecast' : 'results'
  const monthName = text(header?.['month_name']) ?? 'Month'
  const monthLabel = text(header?.['month_label']) ?? ''
  const fytdLabel = text(header?.['fytd_label']) ?? ''
  const pyLabel = text(header?.['py_month_label']) ?? ''

  const byUnit = (key: string) => dataset(result, key).filter((row) => row['measure'] === unit)
  const summary = byUnit('rep_summary')
  const endUsers = byUnit('rep_end_users')
  const productLines = byUnit('rep_product_lines')
  const months = byUnit('rep_months')
  const janusReps = dataset(result, 'janus_reps')
  const janusSkus = dataset(result, 'janus_skus')
  const placements = dataset(result, 'janus_placements')
  const repPlacements = dataset(result, 'janus_rep_placements')
  const skuList = dataset(result, 'janus_sku_list')
  const basePairs = dataset(result, 'janus_base_pairs')

  // With a rep chosen the tiles are that rep's own figures, not the company's.
  const scope = totals(chosen === null ? summary : summary.filter((row) => row['sales_rep'] === chosen))
  const forRep = (rows: Keyed[]) =>
    chosen === null ? rows : rows.filter((row) => row['sales_rep'] === chosen)

  return (
    <div className="bespoke-report srp-page">
      <MailoutBar
        rep={chosen}
        // The mail-out is the finished month's performance (owner, 2026-10-18):
        // on the month in progress it is the last complete month, never this one.
        month={
          view === 'forecast'
            ? 'latest'
            : typeof values['month'] === 'string' && values['month'] !== ''
              ? values['month']
              : 'latest'
        }
      />

      {view === 'forecast' ? (
        <ForecastView
          result={result}
          unit={unit}
          chosen={chosen}
          team={team}
          onSelect={(name) => {
            setParam('rep', name === chosen ? ALL : name)
          }}
        />
      ) : (
        <>
          <p className="srp-period">
            <strong>{monthLabel}</strong> against {pyLabel} and budget · fiscal year to date{' '}
            {fytdLabel} · {UNIT_NAME[unit]} · base business (Janus is reported separately below)
          </p>

          <section className="srp-kpis" aria-label={chosen === null ? 'Company totals' : `${chosen} totals`}>
            <Kpi
              label={`${chosen ?? team ?? 'Company'}: ${monthName}`}
              value={amount(scope['month_actual'] ?? 0, unit)}
              sub={`${pct(ratio(scope['month_actual'] ?? 0, scope['month_budget'] ?? 0))} of budget`}
            />
            <Kpi
              label={`${monthName} vs budget`}
              value={signed(scope['month_var_budget'] ?? 0, unit)}
              sub={`vs PY ${signed(scope['month_var_py'] ?? 0, unit)}`}
              toneValue={scope['month_var_budget'] ?? 0}
            />
            <Kpi
              label="FYTD actual"
              value={amount(scope['fytd_actual'] ?? 0, unit)}
              sub={`${pct(ratio(scope['fytd_actual'] ?? 0, scope['fytd_budget'] ?? 0))} of budget`}
            />
            <Kpi
              label="FYTD vs budget"
              value={signed(scope['fytd_var_budget'] ?? 0, unit)}
              toneValue={scope['fytd_var_budget'] ?? 0}
              sub={`budget ${amount(scope['fytd_budget'] ?? 0, unit)}`}
            />
            <Kpi
              label="FYTD vs prior year"
              value={signed(scope['fytd_var_py'] ?? 0, unit)}
              toneValue={scope['fytd_var_py'] ?? 0}
              sub={`${pct(ratio(scope['fytd_actual'] ?? 0, scope['fytd_py'] ?? 0))} of PY`}
            />
          </section>

          {chosen === null ? (
            <>
              <Card
                title={team === null ? 'All reps' : `${team}: every rep`}
                subtitle="The grey band is the budget, the bar is actual (green once at budget), the orange tick is last year, and the label is actual as a percentage of budget. Click a rep in the table to open them."
              >
                {summary.length === 0 ? (
                  <Empty label="No sales or budget for this period." />
                ) : (
                  <>
                    <BulletPair rows={selling(summary)} unit={unit} monthLabel={monthLabel} />
                    <p className="srp-hint">
                      The charts show reps with sales this year or last. Budget-only lines
                      (placeholders, plugs such as the negative MOSHERA line, and budget the SKU map
                      cannot give a rep) are in the table below.
                    </p>
                    <ComparisonTable
                      rows={summary}
                      keyColumn="sales_rep"
                      keyLabel="Sales rep"
                      unit={unit}
                      monthName={monthName}
                      selected={null}
                      onSelect={(name) => {
                        setParam('rep', name)
                      }}
                    />
                  </>
                )}
              </Card>
              <Card title="Company by month" subtitle="Actual, budget and prior year, every month of the fiscal year.">
                <TrendChart rows={months} unit={unit} />
              </Card>
            </>
          ) : (
            <Card
              title={chosen}
              subtitle={`How ${chosen} is doing: every month of the fiscal year, and what made up the gap to budget. The tables are at the foot of the page.`}
              actions={
                <button
                  type="button"
                  className="srp-clear"
                  onClick={() => {
                    setParam('rep', ALL)
                  }}
                >
                  All reps
                </button>
              }
            >
              <div className="srp-charts srp-charts-even">
                <div className="srp-chart">
                  <p className="srp-chart-title">By month</p>
                  <TrendChart rows={forRep(months)} unit={unit} />
                </div>
                <RepBridge rows={forRep(endUsers)} unit={unit} />
              </div>
              <h3 className="srp-section-title">Largest customers</h3>
              <p className="srp-hint">
                The grey band is the budget, the bar is actual (green once at budget), the orange
                tick is last year, and the label is actual as a percentage of budget.
              </p>
              <BulletPair
                rows={[...forRep(endUsers)].sort((a, b) => rankSize(b) - rankSize(a)).slice(0, 10)}
                unit={unit}
                monthLabel={monthLabel}
                keyColumn="end_user"
                noun="customer"
              />
            </Card>
          )}
        </>
      )}

      {/* Janus at the foot in every month, the open one too (owner, 2026-10-18). */}
      <JanusPictures
        shipTos={dataset(result, 'janus_ship_tos')}
        orderDays={dataset(result, 'janus_order_days')}
        skuList={skuList}
        chosen={chosen}
        unit={unit}
      />

      {view !== 'forecast' && chosen !== null && (
        <Card title={`${chosen}: the detail`} subtitle={`${monthLabel} and fiscal year to date, the workbook's figures.`}>
          <h3 className="srp-section-title">By end user</h3>
          <ComparisonTable
            rows={forRep(endUsers)}
            keyColumn="end_user"
            keyLabel="End user"
            unit={unit}
            monthName={monthName}
            top={10}
          />
          <h3 className="srp-section-title">By product line</h3>
          <ComparisonTable
            rows={forRep(productLines)}
            keyColumn="product_line"
            keyLabel="Product line"
            unit={unit}
            monthName={monthName}
          />
        </Card>
      )}

      <JanusSection
        shipTos={dataset(result, 'janus_ship_tos')}
        orderDays={dataset(result, 'janus_order_days')}
        booked={dataset(result, 'janus_booked')}
        chosen={chosen}
        monthName={monthName}
        reps={janusReps}
        skus={janusSkus}
        placements={placements}
        repPlacements={repPlacements}
        skuList={skuList}
        basePairs={basePairs}
        onSelect={(name) => {
          setParam('rep', name)
        }}
      />

      {result.caveats.length > 0 && (
        <section className="srp-notes" aria-label="About these numbers">
          <h3 className="srp-section-title">About these numbers</h3>
          <CaveatList caveats={result.caveats} />
        </section>
      )}
    </div>
  )
}

const MAILOUT = '/api/mailouts/sales-rep-performance'

/**
 * The monthly mail-out, as downloads. Each is built server-side from this same
 * report run, so a workbook cannot disagree with the page. The email is an
 * Outlook draft (.eml): open it, add the address, send.
 */
function MailoutBar({ rep, month }: { rep: string | null; month: string }) {
  const query = `?month=${encodeURIComponent(month)}`
  const repPath = rep === null ? '' : `${MAILOUT}/reps/${encodeURIComponent(rep)}`
  return (
    <nav className="srp-mailout" aria-label="Monthly mail-out downloads">
      <span className="srp-mailout-label">Mail-out:</span>
      {rep !== null && (
        <>
          <a className="srp-chip" href={`${repPath}/workbook${query}`}>
            {rep} workbook
          </a>
          <a className="srp-chip" href={`${repPath}/email${query}`}>
            {rep} email draft
          </a>
        </>
      )}
      <a className="srp-chip" href={`${MAILOUT}/summary${query}`}>
        All-reps workbook
      </a>
      <a className="srp-chip" href={`${MAILOUT}/bundle${query}`}>
        Every rep, workbooks and email drafts (.zip)
      </a>
      {/* Management's weekly Janus email: placements through today, whatever month is shown. */}
      <a className="srp-chip" href="/api/mailouts/janus/email">
        Janus weekly email draft
      </a>
    </nav>
  )
}

/** How many placements stand where, for the rows on screen. */
function StatusCounts({ rows }: { rows: readonly Keyed[] }) {
  if (rows.length === 0) return null
  const counts = new Map<string, number>()
  for (const row of rows) {
    const status = text(row['reorder_status']) ?? '—'
    counts.set(status, (counts.get(status) ?? 0) + 1)
  }
  return (
    <p className="srp-status-counts">
      {STATUS_ORDER.filter((status) => counts.has(status)).map((status) => (
        <span key={status} className={`srp-pill ${STATUS_TONE[status] ?? ''}`}>
          {status}: <strong>{counts.get(status)}</strong>
        </span>
      ))}
    </p>
  )
}

const FORECAST_VALUES = ['shipped', 'booked', 'projected', 'month_end', 'budget'] as const

function sumRows(rows: readonly Keyed[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const key of FORECAST_VALUES) {
    out[key] = rows.reduce((total, row) => total + (num(row[key]) ?? 0), 0)
  }
  out['variance'] = (out['month_end'] ?? 0) - (out['budget'] ?? 0)
  return out
}

/**
 * The month in progress (`view=forecast`): shipped + booked + projected
 * remainder, without Janus, against the month's budget without Janus.
 *
 * The projection is the sales agents' no-Janus forecast (A3X), split from
 * ship-to to rep by each rep's last six months there. Rep and account
 * projections are estimates; the agent's range exists for the company only.
 */
function ForecastView({
  result,
  unit,
  chosen,
  team,
  onSelect,
}: {
  result: CustomReportProps['result']
  unit: Unit
  chosen: string | null
  team: string | null
  onSelect: (name: string) => void
}) {
  const header = dataset(result, 'fc_header')[0]
  const reps = dataset(result, 'fc_reps').filter((row) => row['measure'] === unit)
  const accounts = dataset(result, 'fc_accounts').filter(
    (row) => row['measure'] === unit && (chosen === null || row['sales_rep'] === chosen),
  )
  const hasForecast = text(header?.['has_forecast']) === 'true'
  const hasBudget = text(header?.['has_budget']) === 'true'
  const month = text(header?.['month_label']) ?? 'This month'
  // With a rep chosen the tiles are that rep's month, not the company's.
  const total = sumRows(chosen === null ? reps : reps.filter((row) => row['sales_rep'] === chosen))
  const selling = reps.filter(
    (row) => Math.abs(num(row['month_end']) ?? 0) + Math.abs(num(row['budget']) ?? 0) > 0.5,
  )
  // The chart leaves out rows too small to draw (a few hundred gallons of
  // budget on a placeholder rep); the table below still lists every one.
  const largest = Math.max(1, ...selling.map((row) => Math.abs(num(row['month_end']) ?? 0)))
  const charted = selling.filter(
    (row) =>
      Math.max(Math.abs(num(row['month_end']) ?? 0), Math.abs(num(row['budget']) ?? 0)) >
      largest * 0.005,
  )
  const g = (key: string) => num(header?.[key]) ?? 0

  return (
    <>
      <p className="srp-period">
        <strong>{month}</strong>, month in progress, without Janus · shipped and booked as of
        now · projected remainder from the sales agents&apos; month-end forecast
        {hasForecast ? ` (data through ${text(header?.['watermark']) ?? '—'})` : ' (not available)'} ·{' '}
        {UNIT_NAME[unit]}
      </p>

      <section className="srp-kpis" aria-label="This month">
        <Kpi label="Shipped" value={amount(total['shipped'] ?? 0, unit)} />
        <Kpi label="Booked, not shipped" value={amount(total['booked'] ?? 0, unit)} />
        <Kpi
          label="Projected remaining"
          value={amount(total['projected'] ?? 0, unit)}
          sub="an estimate"
        />
        <Kpi
          label="Projected month-end"
          value={amount(total['month_end'] ?? 0, unit)}
          sub={
            // The agent's range is for the whole company, never a team's share of it.
            hasForecast && unit === 'gal' && team === null && chosen === null
              ? `agent range ${amount(g('band_low_gallons'), unit)}–${amount(g('band_high_gallons'), unit)}`
              : undefined
          }
        />
        <Kpi
          label="vs budget"
          value={hasBudget ? signed(total['variance'] ?? 0, unit) : 'No budget'}
          toneValue={hasBudget ? (total['variance'] ?? 0) : undefined}
          sub={
            hasBudget
              ? `${pct(ratio(total['month_end'] ?? 0, total['budget'] ?? 0))} of ${amount(total['budget'] ?? 0, unit)}`
              : 'no budget loaded for this month'
          }
        />
      </section>

      {chosen === null && (
      <Card
        title={team === null ? 'All reps: this month' : `${team}: this month`}
        subtitle="Shipped, booked and the projected remainder against each rep's budget for the month. Click a rep in the table to see their accounts."
      >
        {selling.length === 0 ? (
          <Empty label="Nothing shipped, booked, projected or budgeted this month yet." />
        ) : (
          <>
            <ProgressBullets
              rows={charted.filter((row) => row['sales_rep'] !== UNATTRIBUTED)}
              unit={unit}
              title={`${month} by rep`}
              label={`${month} progress by rep`}
            />
            <ForecastTable
              rows={selling}
              keyColumn="sales_rep"
              keyLabel="Sales rep"
              unit={unit}
              selected={chosen}
              onSelect={onSelect}
              repColumns
            />
            <p className="srp-hint">
              Rep projections are estimates: the agent forecasts each ship-to, and a ship-to
              shared by reps is split by their last six months there (
              {String(num(header?.['ship_tos_one_rep']) ?? 0)} of{' '}
              {String(num(header?.['ship_tos']) ?? 0)} ship-tos have one rep). &ldquo;Not yet
              attributable&rdquo; is what the agent expects but cannot place at any ship-to.{' '}
              {g('absorbed_gallons') > 0.5 &&
                `${amount(g('absorbed_gallons'), 'gal')} gal the agent predicted has since been booked or shipped, and is counted once. `}
              Janus is excluded throughout ({amount(g('janus_gallons_excluded'), 'gal')} gal of
              it in the agent&apos;s month) and shown in the last column and below.
            </p>
          </>
        )}
      </Card>
      )}

      {chosen !== null && (
        <Card
          title={`${chosen}: accounts this month`}
          subtitle="Where each account stands: what has shipped and is booked, what the agent expects still to come, and whether it can still order in time to ship this month. An estimate, without Janus."
          actions={
            <button
              type="button"
              className="srp-clear"
              onClick={() => {
                onSelect(chosen)
              }}
            >
              All reps
            </button>
          }
        >
          {accounts.length === 0 ? (
            <Empty label="No accounts for this rep this month." />
          ) : (
            <>
              <ProgressBullets
                rows={customerProgress(accounts, 10)}
                unit={unit}
                title="Largest customers this month"
                label={`${chosen} customers this month`}
                keyColumn="end_user"
              />
              <VarianceBars
                title="Biggest gaps to this month's budget"
                rows={topMovers(
                  accounts.map((row) => ({
                    ...row,
                    _label: text(row['ship_to_customer']) ?? text(row['ship_to_number']) ?? '—',
                    fytd_var_budget: row['variance'] ?? null,
                  })),
                  '_label',
                )}
                unit={unit}
              />
              <AccountTable rows={accounts} unit={unit} />
            </>
          )}
        </Card>
      )}
    </>
  )
}

function ForecastTable({
  rows,
  keyColumn,
  keyLabel,
  unit,
  selected = null,
  onSelect,
  repColumns = false,
}: {
  rows: readonly Keyed[]
  keyColumn: string
  keyLabel: string
  unit: Unit
  selected?: string | null
  onSelect?: (name: string) => void
  repColumns?: boolean
}) {
  const total = sumRows(rows)
  const janusTotal = rows.reduce((sum, row) => sum + (num(row['janus_month']) ?? 0), 0)
  const cells = (row: Record<string, Cell | number | undefined>) => {
    const v = (key: string) => num(row[key])
    return (
      <>
        <td className="cell-number">{amount(v('shipped'), unit)}</td>
        <td className="cell-number">{amount(v('booked'), unit)}</td>
        <td className="cell-number srp-dim">{amount(v('projected'), unit)}</td>
        <td className="cell-number srp-strong">{amount(v('month_end'), unit)}</td>
        <td className="cell-number srp-dim">{amount(v('budget'), unit)}</td>
        <td className={`cell-number ${tone(v('variance'))}`}>{signed(v('variance'), unit)}</td>
        <td className="cell-number">{pct(ratio(v('month_end') ?? 0, v('budget') ?? 0))}</td>
      </>
    )
  }
  return (
    <div className="srp-scroll">
      <table className="srp-table">
        <thead>
          <tr>
            <th>{keyLabel}</th>
            <th className="cell-number">Shipped</th>
            <th className="cell-number">Booked</th>
            <th className="cell-number">Projected remaining (est.)</th>
            <th className="cell-number">Projected month-end</th>
            <th className="cell-number">Budget</th>
            <th className="cell-number">vs budget</th>
            <th className="cell-number">% of budget</th>
            {repColumns && <th className="cell-number">Can still order</th>}
            {repColumns && <th className="cell-number">Janus this month</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const name = text(row[keyColumn]) ?? '—'
            const clickable = onSelect !== undefined && name !== 'Not yet attributable'
            return (
              <tr key={name} className={selected === name ? 'srp-row is-selected' : 'srp-row'}>
                <td>
                  {clickable ? (
                    <button
                      type="button"
                      className="srp-link"
                      onClick={() => {
                        onSelect(name)
                      }}
                    >
                      {name}
                    </button>
                  ) : (
                    <span className="srp-dim">{name}</span>
                  )}
                </td>
                {cells(row)}
                {repColumns && (
                  <td className="cell-number srp-dim">
                    {(num(row['accounts']) ?? 0) > 0
                      ? `${String(num(row['can_still_order']) ?? 0)} of ${String(num(row['accounts']) ?? 0)}`
                      : '—'}
                  </td>
                )}
                {repColumns && (
                  <td className="cell-number srp-dim">{amount(num(row['janus_month']), unit)}</td>
                )}
              </tr>
            )
          })}
          <tr className="srp-total">
            <td>Total</td>
            {cells(total)}
            {repColumns && <td />}
            {repColumns && <td className="cell-number">{amount(janusTotal, unit)}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const OUTLOOK_TONE: Readonly<Record<string, string>> = {
  'Past cutoff: done for the month': 'srp-dim',
  'No further order expected': 'srp-dim',
  'Could still order; none predicted': 'srp-under',
}

function AccountTable({ rows, unit }: { rows: readonly Keyed[]; unit: Unit }) {
  const total = sumRows(rows)
  return (
    <div className="srp-scroll srp-tall">
      <table className="srp-table">
        <thead>
          <tr>
            <th>End user</th>
            <th>Ship-to</th>
            <th className="cell-number">Shipped</th>
            <th className="cell-number">Booked</th>
            <th className="cell-number">Projected (est.)</th>
            <th className="cell-number">Month-end</th>
            <th className="cell-number">Budget</th>
            <th className="cell-number">vs budget</th>
            <th>Outlook</th>
            <th>Can order in time?</th>
            <th className="cell-number">Orders left</th>
            <th>Ordering</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const outlook = text(row['outlook']) ?? '—'
            const cutoff = text(row['cutoff_status'])
            const interval = num(row['expected_interval'])
            return (
              <tr key={`${text(row['ship_to_number']) ?? ''}-${text(row['end_user']) ?? ''}`}>
                <td className="srp-wrap">{text(row['end_user'])}</td>
                <td className="srp-wrap">{text(row['ship_to_customer']) ?? text(row['ship_to_number'])}</td>
                <td className="cell-number">{amount(num(row['shipped']), unit)}</td>
                <td className="cell-number">{amount(num(row['booked']), unit)}</td>
                <td className="cell-number srp-dim">{amount(num(row['projected']), unit)}</td>
                <td className="cell-number srp-strong">{amount(num(row['month_end']), unit)}</td>
                <td className="cell-number srp-dim">{amount(num(row['budget']), unit)}</td>
                <td className={`cell-number ${tone(num(row['variance']))}`}>
                  {signed(num(row['variance']), unit)}
                </td>
                <td className={`srp-wrap ${OUTLOOK_TONE[outlook] ?? 'srp-over'}`}>{outlook}</td>
                <td
                  className={cutoff === 'Past cutoff' ? 'srp-dim' : ''}
                  title={
                    cutoff === 'Can still order' && text(row['closes_on']) !== null
                      ? `An order placed today ships about ${text(row['closes_on']) ?? ''}`
                      : undefined
                  }
                >
                  {cutoff === 'Can still order' ? 'Yes' : cutoff === 'Past cutoff' ? 'No, past cutoff' : '—'}
                </td>
                <td className="cell-number">{amount(num(row['orders_left']), 'cases')}</td>
                <td className="srp-wrap" title={text(row['cadence_class']) ?? undefined}>
                  {interval === null ? '—' : `every ${String(Math.round(interval))} days`}
                  <span className="srp-dim">
                    {' · last '}
                    {text(row['last_order_date']) ?? '—'}
                    {num(row['days_since_last']) !== null &&
                      ` (${String(num(row['days_since_last']))}d)`}
                  </span>
                </td>
              </tr>
            )
          })}
          <tr className="srp-total">
            <td colSpan={2}>Total</td>
            <td className="cell-number">{amount(total['shipped'] ?? 0, unit)}</td>
            <td className="cell-number">{amount(total['booked'] ?? 0, unit)}</td>
            <td className="cell-number">{amount(total['projected'] ?? 0, unit)}</td>
            <td className="cell-number">{amount(total['month_end'] ?? 0, unit)}</td>
            <td className="cell-number">{amount(total['budget'] ?? 0, unit)}</td>
            <td className={`cell-number ${tone(total['variance'] ?? 0)}`}>
              {signed(total['variance'] ?? 0, unit)}
            </td>
            <td colSpan={4} />
          </tr>
        </tbody>
      </table>
    </div>
  )
}


/** The workbook's two blocks — the month and FYTD — side by side, plus % of budget. */
function ComparisonTable({
  rows,
  keyColumn,
  keyLabel,
  unit,
  monthName,
  selected = null,
  onSelect,
  top,
}: {
  rows: readonly Keyed[]
  keyColumn: string
  keyLabel: string
  unit: Unit
  monthName: string
  selected?: string | null
  onSelect?: (name: string) => void
  /** Show this many rows, then one "All others" row that expands. */
  top?: number
}) {
  const [expanded, setExpanded] = useState(false)
  if (rows.length === 0) return <Empty label="Nothing to compare for this selection." />
  const total = totals(rows)
  // Ranked by the largest of FYTD actual, prior year and budget, so an account
  // budgeted but not yet shipping, or big last year and gone quiet, stays in
  // the top rows instead of disappearing into All others (owner, 2026-10-17).
  const ranked =
    top === undefined
      ? rows
      : [...rows].sort((a, b) => rankSize(b) - rankSize(a))
  const shown = top === undefined || expanded || ranked.length <= top + 1 ? ranked : ranked.slice(0, top)
  const rest = ranked.slice(shown.length)
  const cells = (row: Record<string, Cell | number | undefined>) => {
    const v = (key: string) => num(row[key])
    return (
      <>
        <td className="cell-number">{amount(v('month_actual'), unit)}</td>
        <td className="cell-number srp-dim">{amount(v('month_py'), unit)}</td>
        <td className="cell-number srp-dim">{amount(v('month_budget'), unit)}</td>
        <td className={`cell-number ${tone(v('month_var_py'))}`}>{signed(v('month_var_py'), unit)}</td>
        <td className={`cell-number ${tone(v('month_var_budget'))}`}>
          {signed(v('month_var_budget'), unit)}
        </td>
        <td className="cell-number srp-gap">{amount(v('fytd_actual'), unit)}</td>
        <td className="cell-number srp-dim">{amount(v('fytd_py'), unit)}</td>
        <td className="cell-number srp-dim">{amount(v('fytd_budget'), unit)}</td>
        <td className={`cell-number ${tone(v('fytd_var_py'))}`}>{signed(v('fytd_var_py'), unit)}</td>
        <td className={`cell-number ${tone(v('fytd_var_budget'))}`}>
          {signed(v('fytd_var_budget'), unit)}
        </td>
        <td className="cell-number">
          {pct(ratio(v('fytd_actual') ?? 0, v('fytd_budget') ?? 0))}
        </td>
      </>
    )
  }
  return (
    <div className="srp-scroll">
      <table className="srp-table">
        <thead>
          <tr>
            <th rowSpan={2}>{keyLabel}</th>
            <th colSpan={5} className="srp-group">
              Month: {monthName}
            </th>
            <th colSpan={6} className="srp-group srp-gap">
              Fiscal year to date
            </th>
          </tr>
          <tr>
            <th className="cell-number">Actual</th>
            <th className="cell-number">Prior year</th>
            <th className="cell-number">Budget</th>
            <th className="cell-number">Actual v PY</th>
            <th className="cell-number">Actual v budget</th>
            <th className="cell-number srp-gap">FYTD</th>
            <th className="cell-number">FYTD PY</th>
            <th className="cell-number">FYTD budget</th>
            <th className="cell-number">FYTD v PY</th>
            <th className="cell-number">FYTD v budget</th>
            <th className="cell-number">% of budget</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const name = text(row[keyColumn]) ?? '—'
            return (
              <tr key={name} className={selected === name ? 'srp-row is-selected' : 'srp-row'}>
                <td>
                  {onSelect === undefined ? (
                    name
                  ) : (
                    <button
                      type="button"
                      className="srp-link"
                      onClick={() => {
                        onSelect(name)
                      }}
                    >
                      {name}
                    </button>
                  )}
                </td>
                {cells(row)}
              </tr>
            )
          })}
          {rest.length > 0 && (
            <tr className="srp-others">
              <td>
                <button
                  type="button"
                  className="srp-link"
                  aria-expanded={false}
                  onClick={() => {
                    setExpanded(true)
                  }}
                >
                  All others ({rest.length})
                </button>
              </td>
              {cells(totals(rest))}
            </tr>
          )}
          {expanded && top !== undefined && ranked.length > top + 1 && (
            <tr>
              <td colSpan={12}>
                <button
                  type="button"
                  className="srp-link"
                  onClick={() => {
                    setExpanded(false)
                  }}
                >
                  Show the top {top} only
                </button>
              </td>
            </tr>
          )}
          <tr className="srp-total">
            <td>Grand total</td>
            {cells(total)}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/** Reps with sales this fiscal year or last; budget-only lines stay out of the charts. */
/** The month-in-progress rows that are reps (not the agent's unattributable lump). */
const UNATTRIBUTED = 'Not yet attributable'

const PROGRESS_KEYS = ['shipped', 'booked', 'projected', 'month_end', 'budget'] as const

/**
 * A rep's customers this month, from the forecast's account rows (one per
 * ship-to): summed to the end user, largest of month-end or budget first.
 */
function customerProgress(accounts: readonly Keyed[], top: number): Keyed[] {
  const byCustomer = new Map<string, Record<string, number>>()
  for (const row of accounts) {
    const name = text(row['end_user']) ?? '—'
    const sums = byCustomer.get(name) ?? { shipped: 0, booked: 0, projected: 0, month_end: 0, budget: 0 }
    for (const key of PROGRESS_KEYS) sums[key] = (sums[key] ?? 0) + (num(row[key]) ?? 0)
    byCustomer.set(name, sums)
  }
  return [...byCustomer.entries()]
    .map(([end_user, sums]): Keyed => ({ end_user, ...sums }))
    .filter((row) => Math.max(num(row['month_end']) ?? 0, num(row['budget']) ?? 0) > 0.5)
    .sort(
      (a, b) =>
        Math.max(num(b['month_end']) ?? 0, num(b['budget']) ?? 0) -
        Math.max(num(a['month_end']) ?? 0, num(a['budget']) ?? 0),
    )
    .slice(0, top)
}

function selling(rows: readonly Keyed[]): Keyed[] {
  return rows.filter(
    (row) => Math.abs(num(row['fytd_actual']) ?? 0) + Math.abs(num(row['fytd_py']) ?? 0) > 0.5,
  )
}

interface Mover {
  readonly name: string
  readonly value: number
}

/** The largest contributors to the FYTD variance against budget, either way. */
function topMovers(rows: readonly Keyed[], key: string, count = 6): Mover[] {
  const movers = rows
    .map((row) => ({ name: text(row[key]) ?? '—', value: num(row['fytd_var_budget']) ?? 0 }))
    .filter((m) => Math.round(m.value) !== 0)
  const up = movers.filter((m) => m.value > 0).sort((a, b) => b.value - a.value).slice(0, count)
  const down = movers.filter((m) => m.value < 0).sort((a, b) => a.value - b.value).slice(0, count)
  return [...up, ...down]
}

function VarianceBars({
  title,
  rows,
  unit,
}: {
  title: string
  rows: readonly Mover[]
  unit: Unit
}) {
  const ordered = [...rows].sort((a, b) => a.value - b.value)
  if (ordered.length === 0) {
    return (
      <div className="srp-chart">
        <p className="srp-chart-title">{title}</p>
        <Empty label="Nothing over or under budget." />
      </div>
    )
  }
  const option: EChartsOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { ...GRID, top: 8 },
    xAxis: { type: 'value', axisLabel: { formatter: axisFormatter(unit), hideOverlap: true } },
    yAxis: {
      type: 'category',
      data: ordered.map((m) => m.name),
      axisLabel: { width: 190, overflow: 'truncate' },
    },
    series: [
      {
        name: 'vs budget',
        type: 'bar',
        data: ordered.map((m) => ({
          value: Math.round(m.value),
          itemStyle: { color: m.value >= 0 ? COLOURS.over : COLOURS.under },
        })),
      },
    ],
  }
  return (
    <div className="srp-chart">
      <p className="srp-chart-title">{title}</p>
      <ChartFrame option={option} label={title} height={Math.max(200, ordered.length * 30 + 40)} />
    </div>
  )
}

/** Monthly bars for actual, lines for budget and prior year, summed over the rows given. */
function TrendChart({ rows, unit }: { rows: readonly Keyed[]; unit: Unit }) {
  const series = useMemo(() => {
    const byMonth = new Map<string, { label: string; actual: number | null; py: number; budget: number }>()
    for (const row of rows) {
      const month = text(row['month']) ?? ''
      const current = byMonth.get(month) ?? {
        label: text(row['label']) ?? month,
        actual: null,
        py: 0,
        budget: 0,
      }
      const actual = num(row['actual'])
      current.actual = actual === null ? current.actual : (current.actual ?? 0) + actual
      current.py += num(row['py']) ?? 0
      current.budget += num(row['budget']) ?? 0
      byMonth.set(month, current)
    }
    return [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v)
  }, [rows])
  if (series.length === 0) return <Empty label="No monthly figures for this selection." />
  const option: EChartsOption = {
    tooltip: { trigger: 'axis' },
    legend: { ...LEGEND_TOP, data: ['Actual', 'Budget', 'Prior year'] },
    grid: GRID,
    xAxis: { type: 'category', data: series.map((s) => s.label.split(' ')[0] ?? s.label) },
    yAxis: { type: 'value', axisLabel: { formatter: axisFormatter(unit) } },
    series: [
      { name: 'Actual', type: 'bar', data: series.map((s) => s.actual), itemStyle: { color: COLOURS.actual } },
      { name: 'Budget', type: 'line', data: series.map((s) => s.budget), itemStyle: { color: COLOURS.budget }, lineStyle: { type: 'dashed' } },
      { name: 'Prior year', type: 'line', data: series.map((s) => s.py), itemStyle: { color: COLOURS.py } },
    ],
  }
  return <ChartFrame option={option} label="Actual, budget and prior year by month" height={280} />
}

/**
 * One rep's Janus ship-tos, one row each (owner, 2026-10-18: "we don't need
 * to show all salespeople ... just the customers for that salesperson. Ship
 * To, Orders, first order date, reorders"). Every date is a ship date (owner,
 * 2026-10-19): shipments are the ship dates since launch, and a reorder is a
 * ship date that reordered at least one SKU.
 */
function RepShipTos({
  shipTos,
  orderDays,
  skuCount,
}: {
  shipTos: readonly Keyed[]
  orderDays: readonly Keyed[]
  skuCount: number
}) {
  const days = (shipTo: string) => orderDays.filter((row) => text(row['ship_to_number']) === shipTo)
  const rows = [...shipTos].sort((a, b) => (num(b['total_cases']) ?? 0) - (num(a['total_cases']) ?? 0))
  return (
    <div className="srp-scroll">
      <table className="srp-table">
        <thead>
          <tr>
            <th>Ship-to</th>
            <th>Customer</th>
            <th className="cell-number">SKUs</th>
            <th className="cell-number">Shipments</th>
            <th>First shipped</th>
            <th className="cell-number">Opening cases</th>
            <th className="cell-number">Reorders</th>
            <th className="cell-number">Reorder cases</th>
            <th>Last shipped</th>
            <th>Booked, not shipped</th>
            <th className="cell-number">Total cases</th>
            <th>Where it stands</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const shipTo = text(row['ship_to_number']) ?? ''
            const orders = days(shipTo)
            const reorders = orders.filter((d) => text(d['kind']) !== 'Opening order').length
            const status = text(row['status']) ?? ''
            return (
              <tr key={shipTo}>
                <td className="srp-wrap">{text(row['ship_to_customer']) ?? shipTo}</td>
                <td className="srp-wrap">{text(row['end_user'])}</td>
                <td className="cell-number">
                  {String(num(row['skus']) ?? 0)} of {String(skuCount)}
                </td>
                <td className="cell-number">{String(orders.length)}</td>
                <td>{text(row['first_order_date'])}</td>
                <td className="cell-number">{amount(num(row['opening_cases']), 'cases')}</td>
                <td className="cell-number">{reorders > 0 ? String(reorders) : 'none yet'}</td>
                <td className="cell-number">{amount(num(row['reorder_cases']), 'cases')}</td>
                <td>{text(row['last_order_date'])}</td>
                <td>
                  {(num(row['booked_cases']) ?? 0) > 0
                    ? `${amount(num(row['booked_cases']), 'cases')} cs, ships ${text(row['booked_ship_date']) ?? ''}`
                    : ''}
                </td>
                <td className="cell-number srp-strong">{amount(num(row['total_cases']), 'cases')}</td>
                <td>
                  <span className="srp-pill" style={{ borderColor: statusColour(status), color: statusColour(status) }}>
                    {status}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Janus orders booked and not yet shipped (owner, 2026-10-19): a reorder, a
 * new SKU at a Janus ship-to, or a ship-to's first Janus order. Base business
 * is never here. Nothing is shown when nothing is on order.
 */
function BookedTable({ rows, showRep }: { rows: readonly Keyed[]; showRep: boolean }) {
  if (rows.length === 0) return null
  return (
    <>
      <h3 className="srp-section-title">Booked, not yet shipped ({rows.length})</h3>
      <div className="srp-scroll">
        <table className="srp-table">
          <thead>
            <tr>
              {showRep && <th>Sales rep</th>}
              <th>Ship-to</th>
              <th>Customer</th>
              <th>SKU</th>
              <th>Booked</th>
              <th>Ordered</th>
              <th>Ships</th>
              <th className="cell-number">Cases</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${text(row['sales_rep']) ?? ''}-${text(row['ship_to_number']) ?? ''}-${text(row['sku']) ?? ''}`}>
                {showRep && <td>{text(row['sales_rep'])}</td>}
                <td className="srp-wrap">{text(row['ship_to_customer']) ?? text(row['ship_to_number'])}</td>
                <td className="srp-wrap">{text(row['end_user'])}</td>
                <td className="srp-wrap" title={text(row['item']) ?? ''}>
                  {shortItem(text(row['sku']), text(row['item']))}
                </td>
                <td>{text(row['kind'])}</td>
                <td>{text(row['order_date'])}</td>
                <td>{text(row['ship_date'])}</td>
                <td className="cell-number">{amount(num(row['cases']), 'cases')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

function JanusSection({
  chosen,
  monthName,
  reps,
  skus,
  placements,
  repPlacements,
  skuList,
  basePairs,
  shipTos,
  orderDays,
  booked,
  onSelect,
}: {
  shipTos: readonly Keyed[]
  orderDays: readonly Keyed[]
  booked: readonly Keyed[]
  chosen: string | null
  monthName: string
  reps: readonly Keyed[]
  skus: readonly Keyed[]
  placements: readonly Keyed[]
  repPlacements: readonly Keyed[]
  skuList: readonly Keyed[]
  basePairs: readonly Keyed[]
  onSelect: (name: string) => void
}) {
  const repNames = [
    ...new Set([
      ...reps.map((row) => text(row['sales_rep']) ?? ''),
      ...repPlacements.map((row) => text(row['sales_rep']) ?? ''),
    ]),
  ].filter((name) => name !== '')
  const volume = (name: string, measure: string, column: string) =>
    num(
      reps.find((row) => row['sales_rep'] === name && row['measure'] === measure)?.[column],
    ) ?? 0
  const placed = (name: string) => repPlacements.find((row) => row['sales_rep'] === name)
  const shown = chosen === null ? placements : placements.filter((row) => row['sales_rep'] === chosen)
  const repSkus = skus.filter(
    // Janus is counted in cases, whatever unit the page is in (owner,
    // 2026-10-18: a gallon figure under a cases heading read as 2,000 cases).
    (row) => (chosen === null || row['sales_rep'] === chosen) && row['measure'] === 'cases',
  )
  const ordered = [...repNames].sort((a, b) => volume(b, 'cases', 'since_launch') - volume(a, 'cases', 'since_launch'))

  const option: EChartsOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { ...LEGEND_TOP, data: ['Opening cases', 'Reorder cases'] },
    grid: GRID,
    xAxis: { type: 'value', axisLabel: { hideOverlap: true } },
    yAxis: { type: 'category', data: [...ordered].reverse() },
    series: [
      {
        name: 'Opening cases',
        type: 'bar',
        stack: 'cases',
        data: [...ordered].reverse().map((name) => num(placed(name)?.['opening_cases']) ?? 0),
        itemStyle: { color: COLOURS.py },
      },
      {
        name: 'Reorder cases',
        type: 'bar',
        stack: 'cases',
        data: [...ordered].reverse().map((name) => num(placed(name)?.['reorder_cases']) ?? 0),
        itemStyle: { color: COLOURS.over },
      },
    ],
  }

  return (
    <Card
      title={chosen === null ? 'Janus detail: every rep, ship-to and SKU' : `Janus detail: ${chosen}'s ship-tos`}
      subtitle="The tables behind the Janus pictures above, in cases. Janus is a launch SKU at a ship-to that did not buy it January to July 2026, shipped on or after the 1 August launch."
    >
      {repNames.length === 0 ? (
        <Empty label="No Janus business yet." />
      ) : (
        <>
          {chosen !== null ? (
            <RepShipTos
              shipTos={shipTos.filter((row) => row['sales_rep'] === chosen)}
              orderDays={orderDays.filter((row) => row['sales_rep'] === chosen)}
              skuCount={skuList.length}
            />
          ) : (
          <div className="srp-stack">
            <div className="srp-scroll">
              <table className="srp-table">
                <thead>
                  <tr>
                    <th>Sales rep</th>
                    <th className="cell-number">Cases {monthName}</th>
                    <th className="cell-number">Cases FYTD</th>
                    <th className="cell-number">Cases since launch</th>
                    <th className="cell-number">Ship-tos</th>
                    <th className="cell-number">Ship-tos reordering</th>
                    <th className="cell-number">SKU placements</th>
                    <th className="cell-number">Reordered</th>
                    <th className="cell-number">Reorder rate</th>
                    <th className="cell-number">Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((name) => {
                    const p = placed(name)
                    return (
                      <tr key={name} className="srp-row">
                        <td>
                          <button
                            type="button"
                            className="srp-link"
                            onClick={() => {
                              onSelect(name)
                            }}
                          >
                            {name}
                          </button>
                        </td>
                        <td className="cell-number">{amount(volume(name, 'cases', 'month_actual'), 'cases')}</td>
                        <td className="cell-number">{amount(volume(name, 'cases', 'fytd_actual'), 'cases')}</td>
                        <td className="cell-number">{amount(volume(name, 'cases', 'since_launch'), 'cases')}</td>
                        <td className="cell-number">{amount(num(p?.['ship_tos']), 'cases')}</td>
                        <td className="cell-number">
                          {amount(num(p?.['ship_tos_repeat']), 'cases')}
                        </td>
                        <td className="cell-number">{amount(num(p?.['sku_placements']), 'cases')}</td>
                        <td className="cell-number">{amount(num(p?.['reordered']), 'cases')}</td>
                        <td className="cell-number">{pct(num(p?.['reorder_rate_pct']))}</td>
                        <td className={`cell-number ${(num(p?.['overdue']) ?? 0) > 0 ? 'srp-under' : ''}`}>
                          {amount(num(p?.['overdue']), 'cases')}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="srp-chart">
              <p className="srp-chart-title">Opening vs reorder cases, by rep</p>
              <ChartFrame option={option} label="Janus opening and reorder cases by rep" height={Math.max(200, ordered.length * 30 + 60)} />
            </div>
          </div>
          )}

          {repSkus.length > 0 && (
            <>
              <h3 className="srp-section-title">
                {chosen === null ? 'By SKU and rep' : `${chosen} by SKU`} (cases)
              </h3>
              <div className="srp-scroll">
                <table className="srp-table">
                  <thead>
                    <tr>
                      {chosen === null && <th>Sales rep</th>}
                      <th>SKU</th>
                      <th className="cell-number">{monthName}</th>
                      <th className="cell-number">FYTD</th>
                      <th className="cell-number">Since launch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repSkus.map((row) => (
                      <tr key={`${text(row['sales_rep']) ?? ''}-${text(row['sku']) ?? ''}`}>
                        {chosen === null && <td>{text(row['sales_rep'])}</td>}
                        <td className="srp-wrap">{text(row['item'])}</td>
                        <td className="cell-number">{amount(num(row['month_actual']), 'cases')}</td>
                        <td className="cell-number">{amount(num(row['fytd_actual']), 'cases')}</td>
                        <td className="cell-number">{amount(num(row['since_launch']), 'cases')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <BookedTable rows={chosen === null ? booked : booked.filter((row) => row['sales_rep'] === chosen)} showRep={chosen === null} />

          <h3 className="srp-section-title">
            Placements and reorders — each ship-to and SKU ({shown.length})
          </h3>
          <p className="srp-hint">
            Since launch, by ship date: only shipped orders count. The opening order is a
            ship-to&apos;s first shipment of that SKU, whatever else it bought before; every later
            shipment of the same SKU at the same ship-to is a reorder. Where it stands: a placement with no reorder a month after opening is
            &ldquo;first reorder overdue&rdquo;; one that reorders is due again at its own average
            gap, and &ldquo;overdue&rdquo; a week past that. One with a reorder booked and not yet
            shipped reads &ldquo;Reorder booked&rdquo; instead. Cases.
          </p>
          <StatusCounts rows={shown} />
          {shown.length === 0 ? (
            <Empty label="No Janus placements for this rep yet." />
          ) : (
            <div className="srp-scroll srp-tall">
              <table className="srp-table">
                <thead>
                  <tr>
                    {chosen === null && <th>Sales rep</th>}
                    <th>Customer</th>
                    <th>Ship-to</th>
                    <th>SKU</th>
                    <th>Opening shipment (cases)</th>
                    <th>Reorders (cases)</th>
                    <th>First reorder (days)</th>
                    <th>Last shipped</th>
                    <th>Next due</th>
                    <th>Where it stands</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <tr key={`${text(row['ship_to_number']) ?? ''}-${text(row['sku']) ?? ''}`}>
                      {chosen === null && <td>{text(row['sales_rep'])}</td>}
                      <td className="srp-wrap">{text(row['end_user'])}</td>
                      <td className="srp-wrap">
                        {text(row['ship_to_customer']) ?? text(row['ship_to_number'])}
                      </td>
                      <td className="srp-wrap" title={text(row['item']) ?? ''}>
                        {shortItem(text(row['sku']), text(row['item']))}
                      </td>
                      <td className="srp-wrap">
                        {text(row['first_order_date'])}{' '}
                        <span className="srp-dim">({amount(num(row['opening_cases']), 'cases')})</span>
                      </td>
                      <td>
                        {amount(num(row['reorders']), 'cases')}{' '}
                        <span className="srp-dim">({amount(num(row['reorder_cases']), 'cases')})</span>
                      </td>
                      <td>
                        {text(row['first_reorder_date']) === null
                          ? '—'
                          : `${text(row['first_reorder_date']) ?? ''} (${amount(num(row['days_to_first_reorder']), 'cases')})`}
                      </td>
                      <td className="srp-wrap">
                        {text(row['last_order_date'])}{' '}
                        <span className="srp-dim">({amount(num(row['days_since_last']), 'cases')}d)</span>
                      </td>
                      <td>{text(row['next_due']) ?? '—'}</td>
                      <td className={`srp-wrap ${STATUS_TONE[text(row['reorder_status']) ?? ''] ?? ''}`}>
                        {text(row['reorder_status'])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <details className="srp-details">
        <summary>
          The Janus SKUs, and the {basePairs.length} ship-to and SKU pairs counted as base business
        </summary>
        <ul className="srp-list">
          {skuList.map((row) => (
            <li key={text(row['sku']) ?? ''}>
              {text(row['item'])} — {text(row['launch_type'])}, launched {text(row['launch_start'])}
            </li>
          ))}
        </ul>
        <div className="srp-scroll">
          <table className="srp-table">
            <thead>
              <tr>
                <th>Sales rep</th>
                <th>Customer</th>
                <th>Ship-to</th>
                <th>SKU</th>
                <th className="cell-number">Jan–Jul 2026 gallons</th>
              </tr>
            </thead>
            <tbody>
              {basePairs.map((row) => (
                <tr key={`${text(row['ship_to_number']) ?? ''}-${text(row['sku']) ?? ''}`}>
                  <td>{text(row['sales_rep'])}</td>
                  <td>{text(row['end_user'])}</td>
                  <td>{text(row['ship_to_customer']) ?? text(row['ship_to_number'])}</td>
                  <td>{text(row['sku'])}</td>
                  <td className="cell-number">{amount(num(row['base_gallons']), 'gal')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </Card>
  )
}
